"use client";

import { SafeHydrate } from "@/SafeHydrate";
import * as React from "react";
import { ShieldCheck, Droplets, ThermometerSun, Clock, Wrench, CircleCheck, Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefits = [{
  icon: ShieldCheck,
  title: "25-Year Warranty",
  description: "Enjoy total peace of mind with our 25-year manufacturer's warranty on all roofing materials."
}, {
  icon: Clock,
  title: "Fast Installation",
  description: "Our expert teams complete most roof replacements in just 3 to 5 business days."
}, {
  icon: Wrench,
  title: "Certified Workmanship",
  description: "All our work is covered by a 5-year workmanship warranty, ensuring flawless execution."
}, {
  icon: ThermometerSun,
  title: "Thermal Efficiency",
  description: "Our new roofing systems improve insulation, reducing your energy costs in both summer and winter."
}];
const processSteps = [{
  id: "step-1",
  title: "1. Initial Assessment",
  description: "We perform a complete inspection of your current roof to identify structural weaknesses and prepare a precise replacement plan."
}, {
  id: "step-2",
  title: "2. Safe Removal",
  description: "Our technicians carefully remove the old roofing while protecting your landscaping and the surrounding areas of your property."
}, {
  id: "step-3",
  title: "3. Deck Preparation",
  description: "We inspect and repair the wood support if necessary, then install a high-performance waterproof underlayment."
}, {
  id: "step-4",
  title: "4. System Installation",
  description: "Expert installation of the new covering (shingles or membrane) according to the highest industry standards for maximum durability."
}, {
  id: "step-5",
  title: "5. Final Inspection",
  description: "A foreman checks every detail of the installation and ensures the site is perfectly clean before the final project delivery."
}];
export default function ServiceDetailsUrethaneReplacement() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: processRef,
    isVisible: isProcessVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="252478" id="roof-replacement-details" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="page-container">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <Badge variant="secondary" className="mb-4 text-primary bg-primary-100 hover:bg-primary-100/80 border-none rounded-md">
            Roofing Expertise
          </Badge>
          <h2 className="font-default text-foreground text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            Roof Replacement Details
          </h2>
          <p className="text-muted-foreground font-default text-lg md:text-xl">
            A rigorous process and premium materials to ensure the long-lasting protection of your home against the elements.
          </p>
        </div>

        {}
        <div ref={benefitsRef} className={`grid grid-cols-1 md:grid-cols-2 gap-6 mb-20 transition-all duration-700 delay-100 ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {benefits.map((benefit, index) => <Card key={index} data-index={index} className="border-border shadow-md hover:shadow-lg transition-shadow duration-300 bg-card rounded-md">
              <CardHeader className="pb-4">
                <div className="size-12 rounded-md bg-primary-100 flex items-center justify-center mb-4">
                  <benefit.icon className="size-6 text-primary" />
                </div>
                <CardTitle className="font-default text-xl text-foreground">{benefit.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="font-default text-base text-muted-foreground">
                  {benefit.description}
                </CardDescription>
              </CardContent>
            </Card>)}
        </div>

        <Separator className="mb-20 bg-border" />

        {}
        <div ref={processRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-start transition-all duration-700 delay-200 ${isProcessVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Wrench className="size-6 text-secondary" />
              <h3 className="font-default text-2xl md:text-3xl font-bold text-foreground">
                Our Replacement Process
              </h3>
            </div>
            <p className="text-muted-foreground font-default mb-8">
              We follow a proven methodology to ensure your new roof is installed to the highest quality standards.
            </p>
            
            <Accordion type="single" collapsible className="w-full" defaultValue="step-1">
              {processSteps.map((step, index) => <AccordionItem key={step.id} value={step.id} data-index={index} className="border-border">
                  <AccordionTrigger className="font-default text-lg text-foreground hover:text-secondary transition-colors">
                    {step.title}
                  </AccordionTrigger>
                  <AccordionContent className="font-default text-muted-foreground text-base leading-relaxed">
                    {step.description}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>

          {}
          <div className="space-y-8">
            <div className="relative rounded-md overflow-hidden shadow-xl border border-border aspect-[4/3]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/roof_replacement_installation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/roof_replacement_installation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/roof_replacement_installation.png-wvc-sizes" alt="Professional roofers installing new shingles" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-900/80 to-transparent flex items-end p-6">
                <p className="text-white font-default font-medium text-lg">Professional installation by certified experts.</p>
              </div>
            </div>

            <Card className="bg-primary-100 border-none shadow-md rounded-md">
              <CardContent className="p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <div className="size-16 rounded-md bg-primary flex items-center justify-center shrink-0">
                  <ShieldCheck className="size-8 text-primary-foreground" />
                </div>
                <div>
                  <h4 className="font-default text-xl font-bold text-primary mb-2">
                    Flexible Financing
                  </h4>
                  <p className="font-default text-muted-foreground">
                    We offer personalized payment plans and financing partnerships to make your roofing project accessible today.
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <Link to="/ContactPage" className="w-full sm:w-auto">
                <Button size="lg" className="w-full text-base h-12 gap-2 bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-md font-default">
                  <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                  Get a Free Estimate
                </Button>
              </Link>
              <Link to="/AboutPage" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" className="w-full text-base h-12 gap-2 border-primary text-primary hover:bg-primary-100 rounded-md font-default">
                  Our Quality Commitment
                  <ArrowRight className="size-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

      </div>
    </section>;
}